import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
} from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { ProjectLocationPlanModel } from '../../models/project-location-plan.model';
import { ProjectLocationPlanActions } from './project-location-plan.actions';

export interface ProjectLocationPlanState
  extends EntityState<ProjectLocationPlanModel.PlanDocument> {
  isLoading: boolean;
  isDeleting: boolean;
  hasLoaded: boolean;
  isCreating: boolean;
  isCopying: boolean;
  isUploadingFloorImage: boolean;
  selectedPlan: ProjectLocationPlanModel.PlanDocument | null;
  planPicture: Blob | null;
}

export const projectLocationPlanAdapter: EntityAdapter<ProjectLocationPlanModel.PlanDocument> =
  createEntityAdapter<ProjectLocationPlanModel.PlanDocument>({
    selectId: (plan) => plan._id,
  });

const initialState: ProjectLocationPlanState =
  projectLocationPlanAdapter.getInitialState({
    isLoading: false,
    isDeleting: false,
    hasLoaded: false,
    isCreating: false,
    isCopying: false,
    isUploadingFloorImage: false,
    selectedPlan: null,
    planPicture: null,
  });

export const projectLocationPlanStateReducer = createReducer(
  initialState,
  on(ProjectLocationPlanActions.resetToInitialState, () => ({
    ...initialState,
  })),
  on(ProjectLocationPlanActions.loadProjectLocationPlans, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(
    ProjectLocationPlanActions.loadProjectLocationPlansSuccess,
    (state, { plans }) => {
      return projectLocationPlanAdapter.setAll(plans, {
        ...state,
        isLoading: false,
        hasLoaded: true,
        activeRequestProjectId: null,
      });
    },
  ),
  on(ProjectLocationPlanActions.loadProjectLocationPlansFail, (state) => ({
    ...state,
    isLoading: false,
    hasLoaded: true,
  })),
  on(ProjectLocationPlanActions.createProjectLocationPlan, (state) => ({
    ...state,
    isCreating: true,
  })),
  on(ProjectLocationPlanActions.createProjectLocationPlanSuccess, (state, { plan }) => {
    return projectLocationPlanAdapter.upsertOne(plan, {
      ...state,
      isCreating: false,
    });
  }),
  on(ProjectLocationPlanActions.createProjectLocationPlanFail, (state) => ({
    ...state,
    isCreating: false,
  })),
  on(
    ProjectLocationPlanActions.setFloorImage,
    (state, { planId, floorImageFileName, floorImageDataUrl }) => {
      const changes: Partial<ProjectLocationPlanModel.PlanDocument> = {
        floorImage: floorImageFileName,
        floorImageUrl: floorImageDataUrl,
      };
      const selectedPlan =
        state.selectedPlan?._id === planId
          ? {
              ...state.selectedPlan,
              ...changes,
            }
          : state.selectedPlan;

      return projectLocationPlanAdapter.updateOne(
        {
          id: planId,
          changes,
        },
        {
          ...state,
          selectedPlan,
          planPicture: floorImageDataUrl ? state.planPicture : null,
        },
      );
    },
  ),
  on(ProjectLocationPlanActions.clearFloorImage, (state, { planId }) => {
    const changes: Partial<ProjectLocationPlanModel.PlanDocument> = {
      floorImage: null,
      floorImageUrl: null,
      floorBounds: null,
      floorScale: null,
      floorRotationDeg: null,
      floorOpacity: null,
    };
    const selectedPlan =
      state.selectedPlan?._id === planId
        ? {
            ...state.selectedPlan,
            ...changes,
          }
        : state.selectedPlan;

    return projectLocationPlanAdapter.updateOne(
      {
        id: planId,
        changes,
      },
      {
        ...state,
        selectedPlan,
        planPicture: null,
      },
    );
  }),
  on(ProjectLocationPlanActions.loadSelectedPlan, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(ProjectLocationPlanActions.loadSelectedPlanSuccess, (state, { plan }) => ({
    ...state,
    selectedPlan: plan,
    isLoading: false,
  })),
  on(ProjectLocationPlanActions.loadSelectedPlanFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(ProjectLocationPlanActions.startCopyPlan, (state) => ({
    ...state,
    isCopying: true,
  })),
  on(
    ProjectLocationPlanActions.startCopyPlanSuccess,
    ProjectLocationPlanActions.startCopyPlanFail,
    (state) => ({
      ...state,
      isCopying: false,
    }),
  ),
  on(ProjectLocationPlanActions.editProjectLocationPlan, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(ProjectLocationPlanActions.editProjectLocationPlanSuccess, (state, { plan }) => {
    return projectLocationPlanAdapter.upsertOne(plan, {
      ...state,
      selectedPlan: plan,
      planPicture: plan.floorImage || plan.floorImageUrl ? state.planPicture : null,
      isLoading: false,
    });
  }),
  on(ProjectLocationPlanActions.editProjectLocationPlanFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(ProjectLocationPlanActions.startDeletePlan, (state) => ({
    ...state,
    isDeleting: true,
  })),
  on(ProjectLocationPlanActions.startDeletePlanSuccess, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(ProjectLocationPlanActions.startDeletePlanFail, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(ProjectLocationPlanActions.downloadPlanPictureSuccess, (state, { image }) => ({
    ...state,
    planPicture: image,
  })),
  on(ProjectLocationPlanActions.uploadProjectLocationPlanFloorImage, (state) => ({
    ...state,
    isUploadingFloorImage: true,
  })),
  on(ProjectLocationPlanActions.uploadProjectLocationPlanFloorImageSuccess, (state, { plan }) => {
    return projectLocationPlanAdapter.upsertOne(plan, {
      ...state,
      isUploadingFloorImage: false,
    });
  }),
  on(ProjectLocationPlanActions.uploadProjectLocationPlanFloorImageFail, (state) => ({
    ...state,
    isUploadingFloorImage: false,
  })),
);

export const projectLocationPlanFeature = createFeature({
  name: 'projectLocationPlan',
  reducer: projectLocationPlanStateReducer,
  extraSelectors: ({ selectProjectLocationPlanState }) => {
    const { selectAll, selectEntities } =
      projectLocationPlanAdapter.getSelectors(
        selectProjectLocationPlanState,
      );

    const selectSelectedPlan = createSelector(
      selectProjectLocationPlanState,
      (state): ProjectLocationPlanModel.PlanDocument | null => state.selectedPlan,
    );

    const selectFloorOpacity = createSelector(
      selectProjectLocationPlanState,
      (state): number => state.selectedPlan?.floorOpacity ?? 100
    );

    return {
      selectProjectLocationPlanEntities: selectEntities,
      selectProjectLocationPlans: selectAll,
      selectSelectedPlan,
      selectFloorOpacity,
      selectProjectLocationPlanById: (planId: string) =>
        createSelector(selectEntities, (entities) => {
          return entities[planId] ?? null;
        }),
      selectProjectLocationPlansByLocationId: (locationId: string) =>
        createSelector(selectAll, (plans) => {
          return plans.filter((plan) => plan.locationId === locationId);
        }),
    };
  },
});
